<template>
  <div v-if="!isPageLoading" class="pdp">
    <div v-if="!productData.isServicesProduct" class="pdp__main">
      <product-details-general-section
        :title="productData.title"
        :subtitle="productData.subtitle"
        :images="productData.images"
        @scrollToDetailsRequested="scrollToDetails($refs.details?.$el)"
      />
      <section class="customizations">
        <div class="container">
          <product-details-customizations
            :sectionTitle="getConfigurationTitle"
            :groupList="productData.groupList"
            :customizations="getCustomizations"
            :validations="v$.getCustomizations.$each.$response.$data"
            :isCellProduct="productData.isCellProduct"
            :isValidationRun="isValidationRun"
            :areFieldsHighlighted="areFieldsHighlighted"
            :hasNumericError="hasNumericError"
            :hasComparisonError="hasComparisonError"
            :isSomeChildCustomizationVisible="isSomeChildCustomizationVisible"
            @valueChanged="onCustomizationValueChange"
            @resetAllCustomizationsRequested="resetAllCustomizations"
            @resetSingleAttributeRequested="resetSingleCustomization"
            @toggleGroup="toggleGroup($event)"
          />
          <div ref="skuBlock" class="customizations__sku">
            <div class="customizations__sidebar">
              <product-details-sku-list
                :skuProducts="skuProducts"
                :activeSku="activeSku"
                :isSkuBlockOutlined="isSkuBlockOutlined"
                :isSkuBlockHighlighted="isSkuBlockHighlighted"
                :isInventoryButtonVisible="productData.isInventory"
                :hasBaseSku="hasBaseSku"
                @skuSelected="setSku"
                @inventoryButtonClicked="inventoryRedirect"
              />
              <div class="product-details">
                <h3
                  class="text-color-navy general__block-title text-h5"
                  v-html="$t('product.admetox.productDetailsTitle')"
                />
                <div class="product-details__container">
                  <!-- <div v-if="this.activeSku.code" class="product-details__sku"><span>SKU:&nbsp;</span>{{this.activeSku.code}}</div> -->
                  <div class="product-details__list">
                    <div
                      v-for="(item, customizationIdx) in getCustomizations"
                      :key="item.title"
                      class="product-details__list-item"
                    >
                      <template v-if="showSummaryLine(item)">
                        <p
                          :class="{
                            'product-details__list-item--missing': !isValueSelected(item),
                            'product-details__list-item--highlight':
                              !v$.getCustomizations.$each.$response.$data[customizationIdx].selected
                                .required && isValidationRun,
                          }"
                        >
                          <span>{{ item.title }}:&nbsp;</span>
                          {{ getFieldValue(item) }}
                        </p>
                      </template>
                    </div>
                  </div>
                  <div v-if="showAliquotTable" class="qty__table-container">
                    <product-details-qty-table
                      :qtyTable="qtyTable"
                      :isCellProduct="productData.isCellProduct"
                      :isBaseSkuSelected="activeSku === baseSku"
                      :tableLabelUnit="tableLabelUnit"
                      :volPerLotTableFieldTitle="volPerLotTableFieldTitle"
                      :aliquotQtyTableFieldTitle="aliquotQtyTableFieldTitle"
                    />
                  </div>
                </div>
              </div>
              <product-details-price-details
                :priceDetails="getPriceDetails"
                :totalPrice="getTotalPrice"
                :savedSum="getSavedSum"
                :priceCopy="getPriceCopy"
                :activeSku="activeSku"
                :isLoggedIn="sessionStore.isUserLoggedIn"
              />
              <div class="qty">
                <!-- <h3 class="general__block-title text-h5" v-html="$t('common.qtyTitle')"></h3> -->
                <div class="qty__container">
                  <div class="qty__input-wrapper">
                    <span class="qty__title text-description">{{ productData.quantityLabel }}</span>
                    <quantity-input
                      v-model="productQty"
                      :class="{
                        'customize-list--fail': v$.productQty.$invalid,
                        'customizations--invalid':
                          isValidationRun && v$.productQty.$invalid && areFieldsHighlighted,
                      }"
                    />
                    <button
                      v-if="isEditableProduct"
                      class="qty__delete-button"
                      @click="isDeleteEditableProductModalVisible = true"
                    >
                      <i class="icon-close qty__delete" />
                    </button>
                  </div>
                  <div v-if="v$.productQty.maxValue.$invalid" class="form-hint">
                    <span>{{
                      $t('product.addToCartQtyError', {
                        qty: productData.maxQuantity,
                      })
                    }}</span>
                  </div>
                  <div class="product-details__lead-time">
                    <lead-time-label :value="leadTime" />
                  </div>
                  <div
                    class="general__btn-wrapper-small"
                    :class="{ 'general__btn-wrapper--edit': isEditableProduct }"
                  >
                    <div v-if="isEditableProduct" class="general__edit-btn-group">
                      <app-button
                        class="general__quote-btn"
                        :class="{
                          'general__quote-btn--invalid': !isModelValid && activeSku.code,
                        }"
                        type="button"
                        size="middle"
                        :text="
                          activeSku.code
                            ? $t('product.admetox.editBtn')
                            : $t('product.admetox.productsTitle')
                        "
                        :disabled="isPunchoutNonPricedProduct"
                        :title="getDisabledBtnTitle"
                        @clicked="
                          activeSku.code ? updateProduct() : scrollToSkuBlock($refs.skuBlock)
                        "
                      />
                      <app-button
                        class="general__quote-cancel"
                        type="button"
                        size="middle"
                        variant="only-text"
                        :text="$t('product.admetox.cancelBtn')"
                        @clicked="cartRedirect"
                      />
                    </div>
                    <app-button
                      v-else
                      id="product-details-add-to-quote-button"
                      class="general__quote-btn"
                      :class="{
                        'general__quote-btn--invalid': !isModelValid && activeSku.code,
                      }"
                      type="button"
                      size="middle"
                      :text="
                        activeSku.code
                          ? $t('product.admetox.quoteBtn')
                          : $t('product.admetox.productsTitle')
                      "
                      :disabled="isPunchoutNonPricedProduct"
                      :title="getDisabledBtnTitle"
                      @clicked="activeSku.code ? addProduct() : scrollToSkuBlock($refs.skuBlock)"
                    />
                  </div>
                </div>
              </div>

              <loader v-if="isLoaderVisible" :inline="true" />
            </div>
          </div>
        </div>
      </section>
      <product-details-tabs
        ref="details"
        :tabs="productData.tabs"
        :relatedProducts="productData.relatedProducts"
      />
    </div>

    <div v-else class="service__main">
      <interior-hero
        :item="getHeroData"
        :showLearnMoreBtn="hasDescriptionRich"
        learnMoreBtnId="product-details-learn-more-button"
        @scrollToDetailsRequested="scrollToRichText($refs.richText)"
      />
      <section class="customizations">
        <div class="container">
          <product-details-customizations
            :sectionHeader="getServiceConfigurationHeader"
            :groupList="productData.groupList"
            :customizations="getCustomizations"
            :validations="v$.getCustomizations.$each.$response.$data"
            :isCellProduct="productData.isCellProduct"
            :isValidationRun="isValidationRun"
            :areFieldsHighlighted="areFieldsHighlighted"
            :hasNumericError="hasNumericError"
            :hasComparisonError="hasComparisonError"
            @valueChanged="onCustomizationValueChange"
            @resetAllCustomizationsRequested="resetAllCustomizations"
            @resetSingleAttributeRequested="resetSingleCustomization"
            @toggleGroup="toggleGroup($event)"
          />
          <div
            class="general__btn-wrapper-small"
            :class="{ 'general__btn-wrapper--edit': isEditableProduct }"
          >
            <h4 class="general__quote-btn--title" v-html="$t('product.admetox.quoteBtnTitle')" />
            <div v-if="isEditableProduct" class="general__edit-btn-group">
              <app-button
                class="general__quote-btn"
                :class="{
                  'general__quote-btn--invalid': !isModelValid && activeSku.code,
                }"
                type="button"
                size="sub-middle"
                variant="green"
                :text="
                  activeSku.code
                    ? $t('product.admetox.editBtn')
                    : $t('product.admetox.productsTitle')
                "
                :disabled="isPunchoutNonPricedProduct"
                :title="getDisabledBtnTitle"
                @clicked="activeSku.code ? updateProduct() : scrollToSkuBlock($refs.skuBlock)"
              />
              <app-button
                class="general__quote-cancel"
                type="button"
                size="sub-middle"
                variant="only-text"
                :text="$t('product.admetox.cancelBtn')"
                @clicked="cartRedirect"
              />
            </div>
            <app-button
              v-else
              id="product-details-add-to-quote-button"
              class="general__quote-btn"
              :class="{
                'general__quote-btn--invalid': !isModelValid && activeSku.code,
              }"
              type="button"
              size="sub-middle"
              variant="green"
              :text="
                activeSku.code
                  ? $t('product.admetox.quoteBtn')
                  : $t('product.admetox.productsTitle')
              "
              :disabled="isPunchoutNonPricedProduct"
              :title="getDisabledBtnTitle"
              @clicked="activeSku.code ? addProduct() : scrollToSkuBlock($refs.skuBlock)"
            />
          </div>
        </div>
      </section>
      <div v-if="hasDescriptionRich" ref="richText" class="category-page__rich-text">
        <div class="container category-page__rich-text--container">
          <RichRenderer :richTextData="productData.tabs.details.text" />
        </div>
      </div>
      <section-component
        v-if="productData.serviceRelatedProducts?.length"
        title="Related Services and Products"
      >
        <products-carousel :carouselItems="productData.serviceRelatedProducts" />
      </section-component>
    </div>

    <expert :parentComponentName="$options.name" />

    <transition name="modal">
      <modal
        v-if="isDeleteEditableProductModalVisible"
        class="pdp__delete-modal"
        @close="isDeleteEditableProductModalVisible = false"
      >
        <h3 class="modal__title" v-html="$t('common.confirmDelete')" />
        <div class="modal__two-actions-wrap">
          <app-button
            type="button"
            size="sub-middle"
            :text="$t('common.delete')"
            @clicked="deleteEditableProduct"
          />
          <app-button
            type="button"
            size="sub-middle"
            variant="only-text"
            :text="$t('common.cancel')"
            @clicked="isDeleteEditableProductModalVisible = false"
          />
        </div>
      </modal>
    </transition>

    <loader v-if="isGlobalLoaderVisible" />
  </div>
  <div v-else class="pdp__loading">
    <loader />
  </div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core'
import { helpers, requiredIf, maxLength, integer, minValue } from '@vuelidate/validators'
import { nextTick } from 'vue'

import { debounce } from '@/lib/utils/debounce'
import routeUtils from '@/lib/utils/routes'
import scroll from '@/lib/utils/scroll'

import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'
import Expert from '@/components/common/Expert.vue'
import LeadTimeLabel from '@/components/common/LeadTimeLabel.vue'
import Modal from '@/components/common/Modal.vue'
import ProductsCarousel from '@/components/common/ProductsCarousel.vue'
import QuantityInput from '@/components/common/QuantityInput.vue'

import SectionComponent from '@/components/home/ui/SectionComponent.vue'
import InteriorHero from '@/components/InteriorHero.vue'
import { useAddToCart } from '@/components/pdp/composables/useAddToCart'
import { useScrollAndHighlight } from '@/components/pdp/composables/useScrollAndHighlight'
import { useTransformProduct } from '@/components/pdp/composables/useTransformProduct'
import ProductDetailsCustomizations from '@/components/pdp/ProductDetailsCustomizations.vue'
import ProductDetailsGeneralSection from '@/components/pdp/ProductDetailsGeneralSection.vue'
import ProductDetailsPriceDetails from '@/components/pdp/ProductDetailsPriceDetails.vue'
import ProductDetailsQtyTable from '@/components/pdp/ProductDetailsQtyTable.vue'
import ProductDetailsSkuList from '@/components/pdp/ProductDetailsSkuList.vue'
import ProductDetailsTabs from '@/components/pdp/ProductDetailsTabs.vue'

import { useFormat } from '@/composables/useFormat'
import { usePunchout } from '@/composables/usePunchout'
import { useCartStore } from '@/stores/cart'
import { useProductStore } from '@/stores/product'
import { useSessionStore } from '@/stores/session'

export default {
  name: 'ProductDetails',
  components: {
    ProductDetailsTabs,
    ProductDetailsGeneralSection,
    ProductDetailsSkuList,
    ProductDetailsQtyTable,
    ProductDetailsPriceDetails,
    ProductDetailsCustomizations,
    Expert,
    QuantityInput,
    InteriorHero,
    LeadTimeLabel,
    ProductsCarousel,
    SectionComponent,
    Modal,
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'inventory') {
      next()
    } else if (to.name === 'authentication') {
      this.productStore.setCustomizationSnapshot({
        customizations: this.getCustomizations
          .filter((el) => el.selected)
          .map((el) => {
            return {
              title: el.title,
              selected: el.selected,
            }
          }),
        slugPath: window.location.pathname,
        selectedSku: this.activeSku.code,
      })
    } else {
      this.productStore.unsetCustomizationSnapshot()
    }
    next()
  },
  setup() {
    const cartStore = useCartStore()
    const productStore = useProductStore()
    const sessionStore = useSessionStore()
    const { getImageUrl, fetchItems } = useContentfulComponent()
    const { showToastOnAddToCart } = useAddToCart()
    const { isPunchout } = usePunchout()
    const { formatPrice } = useFormat()

    const {
      transformProduct,
      updateTableValue,
      updateQtyTableBySelectedCustomizations,
      isSomeChildCustomizationVisible,
      getAddToCartProductData,
      getPriceParams,
      getVisibleCustomizations,
      getActiveServerCustomizations,
      getSkuFiltersString,
      stripNonNumerics,
      getActiveTableFieldSelectedValue,
      isForceCustom,
      validateCustomizationValue,
      validateConfigurationsChange,
      checkParentSelection,
      displayHiddenFreeText,
    } = useTransformProduct()
    const {
      isSkuBlockOutlined,
      isSkuBlockHighlighted,
      areFieldsHighlighted,
      scrollToDetails,
      scrollToRichText,
      scrollToSkuBlock,
      scrollToFields,
    } = useScrollAndHighlight()

    return {
      cartStore,
      productStore,
      sessionStore,
      getImageUrl,
      v$: useVuelidate(),
      showToastOnAddToCart,
      transformProduct,
      updateTableValue,
      updateQtyTableBySelectedCustomizations,
      isSomeChildCustomizationVisible,
      getAddToCartProductData,
      getPriceParams,
      getVisibleCustomizations,
      getActiveServerCustomizations,
      getSkuFiltersString,
      stripNonNumerics,
      getActiveTableFieldSelectedValue,
      isForceCustom,
      validateCustomizationValue,
      validateConfigurationsChange,
      checkParentSelection,
      displayHiddenFreeText,
      isSkuBlockOutlined,
      isSkuBlockHighlighted,
      areFieldsHighlighted,
      scrollToDetails,
      scrollToRichText,
      scrollToSkuBlock,
      scrollToFields,
      isPunchout,
      formatPrice,
      fetchItems,
    }
  },
  data() {
    return {
      cProduct: null,
      productEntry: {},
      productData: {},
      skuProducts: [],
      activeSku: {},
      baseSku: {},
      productQty: 1,
      slug: routeUtils.getSlug(this.$route.path, 1),
      isEditableProduct: false,
      isValidationRun: false,
      isLoaderVisible: false,
      isGlobalLoaderVisible: true,
      isPageLoading: true,
      failedNumericTest: [],
      failedComparisonTest: [],
      tableLabelUnit: '',
      qtyTable: {
        qtyPerLot: 0,
        aliquots: 1,
        remainder: 0,
        aliquotQty: 0,
      },
      isGetProductErrorOccurred: false,
      activeSetSkuOperations: [],
      isDeleteEditableProductModalVisible: false,
    }
  },
  head() {
    const pageMeta = this.cProduct?.fields?.pageMeta?.fields || {}
    const metaInfo = {}
    if (pageMeta.title) metaInfo.title = pageMeta.title
    if (pageMeta.metaDescription) {
      metaInfo.meta = [{ name: 'description', content: pageMeta.metaDescription || '' }]
    }
    return metaInfo
  },
  computed: {
    isForceCustomActive() {
      return this.isForceCustom(this.getCustomizations)
    },
    hasBaseSku() {
      return !this.productData.noCustomSku
    },
    getAllCustomizations() {
      return this.productData.customizations?.length ? this.productData.customizations : []
    },
    getCustomizations() {
      return this.getVisibleCustomizations(this.productData.customizations)
    },
    activeSkuFields() {
      return this.getCustomizations.filter((el) => el.serverField).map((el) => el.serverField)
    },
    getProductSkuFromSlug() {
      return routeUtils.getSlug(this.$route.path, 2)
    },
    getCartProductData() {
      return this.getAddToCartProductData(
        {
          productImageUrl: this.productData.images[0]?.url,
          customizations: this.productData.customizations,
          sageCode: this.productData.sageCode,
          productUrl: window.location.pathname,
          quantity: this.productQty,
          isCellProduct: this.productData.isCellProduct,
          isVialsPerDonorProduct: this.productData.isVialsPerDonorProduct,
        },
        this.qtyTable,
        this.activeSku,
        this.isBaseSkuSelected,
        this.getTotalPrice,
      )
    },
    isModelValid() {
      return !this.v$.$invalid
    },
    getConfigurationTitle() {
      if (this.isPageLoading) return this.$t('common.loading')
      return this.productData.isServicesProduct
        ? this.$t('product.admetox.customizationTitleServices')
        : this.$t('product.admetox.customizationTitle')
    },
    getServiceConfigurationHeader() {
      return {
        title: this.productData.customizationsTitle,
        subtitle: this.productData.customizationsSubtitle,
      }
    },
    getHeroData() {
      return {
        title: this.productData.title,
        subtitle: this.productData.subtitle,
        imageList: this.productData.images,
        heroImage: this.getImageUrl(this.productData.heroImage),
      }
    },
    hasDescriptionRich() {
      return !!this.productData.tabs.details?.text
    },
    isBaseSkuSelected() {
      return this.activeSku.code && this.activeSku.code === this.baseSku.code
    },
    leadTime() {
      return !this.activeSku.code || this.isBaseSkuSelected
        ? null
        : this.activeSku.leadTime == null
          ? null
          : this.activeSku.leadTime
    },
    showAliquotTable() {
      const isCurrentVolPerLotNumerical = this.stripNonNumerics(
        this.getActiveTableFieldSelectedValue('volPerLot', this.getCustomizations),
      )
      if (!this.productData.isCellProduct && !isCurrentVolPerLotNumerical) return false
      return this.getCustomizations.some(
        (el) =>
          (el.tableField === 'aliquotQty' && el.selected) ||
          (el.tableField === 'customCellsPerLot' && el.selected) ||
          (el.tableField === 'vialsPerLot' && el.selected > 1),
      )
    },
    configurationsRelationshipEntries() {
      const productConfigEntries = this.productData.configurationsRelationship
      const defaultConfigEntries = this.$contentful.service.getPGPConfigurationFieldsMapping()
      return productConfigEntries || defaultConfigEntries
    },
    isMaxProductQuantityValid() {
      if (!this.productData.isMaxQuantityRestricted) return true
      const isQuantityBelowOrEqualMax = this.productQty <= this.productData.maxQuantity
      const affectingFieldCustomization = this.productData.customizations.find(
        (customization) => customization.title === this.productData.maxQuantityAffectingField.title,
      )
      if (!affectingFieldCustomization) return isQuantityBelowOrEqualMax
      const isAffectingFieldCustomizationSelected =
        !!affectingFieldCustomization.selected ||
        typeof affectingFieldCustomization.selected === 'number'
      if (!isAffectingFieldCustomizationSelected) return true
      if (this.productData.maxQuantityAffectingField.isAllValuesAffectMaxQuantity)
        return isQuantityBelowOrEqualMax
      const isAffectingValueSelected = this.productData.maxQuantityAffectingField.values.includes(
        affectingFieldCustomization.selected,
      )
      return isAffectingValueSelected ? isQuantityBelowOrEqualMax : true
    },
    getPriceDetails() {
      const priceDetails = this.activeSku?.priceDetails || {}
      return {
        itemGrossPrice: priceDetails.itemGrossPrice || {},
        itemNetPrice: priceDetails.itemNetPrice || {},
        customerDiscountAmount: {
          currencyIso: priceDetails.customerDiscountAmount?.currencyIso,
          formattedValue: priceDetails.customerDiscountAmount?.formattedValue?.replace('-', ''),
          value: priceDetails.customerDiscountAmount?.value
            ? Math.abs(priceDetails.customerDiscountAmount.value)
            : 0,
        },
        customerDiscountPercent: priceDetails.customerDiscountPercent
          ? Math.abs(priceDetails.customerDiscountPercent)
          : 0,
        isPositiveCustomerDiscount:
          priceDetails.customerDiscountAmount?.value &&
          Math.sign(priceDetails.customerDiscountAmount?.value) === 1,
      }
    },
    getPriceCopy() {
      if (!this.activeSku.code) {
        return this.$t('product.admetox.nonSelectedProductCopy')
      } else if (
        this.productData.isNHAProduct &&
        !this.qtyTable.qtyPerLot &&
        !this.activeSku.priceDetails
      ) {
        return this.$t('product.admetox.nonPricedProductCopy')
      } else if (
        !+this.getPriceDetails.itemGrossPrice.value ||
        this.activeSku.code === this.baseSku.code
      ) {
        return this.isPunchoutNonPricedProduct
          ? this.$t('punchout.disabledAddToCartBtnCopy')
          : this.$t('product.admetox.nonPricedProductCopy')
      } else {
        return ''
      }
    },
    getTotalPrice() {
      const calculatedTotal = +this.getPriceDetails.itemNetPrice.value
        ? (+this.getPriceDetails.itemNetPrice.value * this.productQty).toFixed(2)
        : null
      return calculatedTotal
        ? this.formatPrice(this.getPriceDetails.itemNetPrice.currencyIso, calculatedTotal)
        : ''
    },
    getSavedSum() {
      const calculatedTotalDiscountAmount = +this.getPriceDetails.customerDiscountAmount.value
        ? (+this.getPriceDetails.customerDiscountAmount.value * this.productQty).toFixed(2)
        : ''
      return calculatedTotalDiscountAmount
        ? this.formatPrice(
            this.getPriceDetails.customerDiscountAmount.currencyIso,
            calculatedTotalDiscountAmount,
          )
        : ''
    },
    isPunchoutNonPricedProduct() {
      return this.isPunchout && this.activeSku.code && !+this.getPriceDetails.itemGrossPrice.value
    },
    getDisabledBtnTitle() {
      return this.isPunchoutNonPricedProduct ? this.$t('punchout.disabledAddToCartBtnTitle') : null
    },
    volPerLotTableFieldTitle() {
      return this.getCustomizations.find((el) => el.tableField === 'volPerLot')?.title || ''
    },
    aliquotQtyTableFieldTitle() {
      return this.getCustomizations.find((el) => el.tableField === 'aliquotQty')?.title || ''
    },
  },
  beforeUnmount() {
    const clearProductAnalyticsData = () => {
      this.productStore.setProductAnalyticsData({})
    }
    clearProductAnalyticsData()
  },
  created() {
    const query = {
      skip: 0,
      limit: 1,
      include: 4,
      order: 'sys.createdAt',
      content_type: 'product',
      'fields.slug': this.slug,
    }

    this.fetchItems(query).then((entries) => {
      if (entries && entries[0]) this.cProduct = entries[0]
      this.initData(entries[0])
    })
  },
  mounted() {
    scroll.pageToTop()
    if (!this.productStore.analyticsData.navigatedFromPageTitle)
      this.productStore.setProductAnalyticsData({ navigatedFromPageTitle: 'Direct PGP' })
  },
  methods: {
    async addProduct() {
      if (!this.isModelValid) {
        this.isValidationRun = true
        this.productData.groupList.forEach((group) => {
          if (group) group.collapsed = false
        })
        await nextTick()
        this.scrollToFields()
        return
      }

      try {
        this.isGlobalLoaderVisible = true
        this.isValidationRun = false
        const [responseEntry] = await this.cartStore.addProductsToCart([this.getCartProductData])
        this.showToastOnAddToCart(responseEntry, this.productData.title)
      } finally {
        this.isGlobalLoaderVisible = false
      }
    },
    async updateProduct() {
      if (this.isModelValid) {
        try {
          this.isGlobalLoaderVisible = true
          this.isValidationRun = false
          await this.cartStore.updateCartProduct({
            entryNumber: this.productEntry.number,
            productData: this.getCartProductData,
          })
          this.cartRedirect()
        } finally {
          this.isGlobalLoaderVisible = false
        }
      } else {
        this.isValidationRun = true
        this.scrollToFields()
      }
    },
    async deleteEditableProduct() {
      try {
        this.isDeleteEditableProductModalVisible = false
        this.isGlobalLoaderVisible = true
        await this.cartStore.deleteProductsFromCart({
          [this.productEntry.number]: {},
        })
        this.cartRedirect()
      } finally {
        this.isGlobalLoaderVisible = false
      }
    },
    cartRedirect() {
      this.$router.push({ path: '/cart' })
    },
    inventoryRedirect() {
      const customizationsList = this.getCustomizations
        .filter((el) => el.selected)
        .map((el) => {
          return {
            title: el.title,
            selected: el.selected,
          }
        })
      this.productStore.setCustomizationSnapshot({
        customizations: [...customizationsList, ...this.productData.inventoryData],
        slugPath: window.location.pathname,
      })
      const query = {
        string: this.productData.title,
        sorting: 'Default',
        sortingByDesc: true,
      }
      this.$router.push({
        name: 'inventory',
        query,
        params: { key: new Date().getTime() },
      })
    },
    hasNumericError(item) {
      return this.failedNumericTest.includes(item.tableField) && item.textarea
    },
    hasComparisonError(customization) {
      const isChildWithSameTableFieldVisible = customization.children.some((child) => {
        return this.getCustomizations.some(
          (customization) =>
            child.fields?.displayName && customization.title === child.fields?.displayName,
        )
      })
      return (
        this.failedComparisonTest.includes(customization.tableField) &&
        !isChildWithSameTableFieldVisible
      )
    },
    setTableLabelUnit(selected) {
      this.tableLabelUnit =
        typeof selected === 'string' && selected?.toString()?.toLowerCase().includes('pool')
          ? 'LOT'
          : 'IND'
    },
    showSummaryLine(item) {
      return item.tableField ? !this.showAliquotTable : item.required || item.selected
    },
    transformSku(sku) {
      return { ...sku, url: `/${this.slug}/${sku.code}` }
    },
    resetAttributeValue(attrValue) {
      attrValue.selected = attrValue.defaultValue
      if (attrValue.options?.length === 1) attrValue.selected = attrValue.options[0]
      if (attrValue.tableField) this.resetTableValue(attrValue.tableField)
      if (attrValue.serverField === 'gender') this.tableLabelUnit = ''
    },
    resetTableValue(field) {
      switch (field) {
        case 'volPerLot':
          this.updateTableValue(field, 0, this.qtyTable, this.productData.isCellProduct)
          this.resetErrorArrays(field)
          break
        case 'vialsPerLot':
          this.updateTableValue(field, 1, this.qtyTable, this.productData.isCellProduct)
          break
        case 'toggleAliquot':
        case 'aliquotQty':
        case 'cellQty':
        case 'customCellQty':
          this.updateTableValue('aliquotQty', 0, this.qtyTable, this.productData.isCellProduct)
          this.resetErrorArrays(field)
          break
      }
    },
    resetErrorArrays(field) {
      this.failedNumericTest = this.failedNumericTest.filter((el) => el !== field)
      this.failedComparisonTest = []
    },
    async resetSingleCustomization(el) {
      await this.withPriceUpdate(async () => {
        this.resetAttributeValue(el, false)
        this.displayHiddenFreeText(el.title, this.productData?.customizations)
        this.validateConfigurationsChange(
          this.productData,
          this.productData.customizations,
          this.configurationsRelationshipEntries,
          this.resetTableValue,
        )
        await this.updateSkuBox(el)
      })
    },
    async resetAllCustomizations() {
      await this.withPriceUpdate(async () => {
        this.getAllCustomizations.forEach((el) => {
          this.resetAttributeValue(el)
          this.displayHiddenFreeText(el.title, this.productData?.customizations)
          this.validateConfigurationsChange(
            this.productData,
            this.productData.customizations,
            this.configurationsRelationshipEntries,
            this.resetTableValue,
          )
        })
        await this.unsetSku()
      })
    },
    async unsetSku() {
      await this.setSku(null)
    },
    async setBaseSku() {
      if (this.hasBaseSku) {
        await this.setSku(this.baseSku.code)
      } else {
        await this.unsetSku()
      }
    },
    async setSku(code, { isLoadAllSkus = false } = {}) {
      const uid = Date.now()
      try {
        this.activeSetSkuOperations.push(uid)
        this.isLoaderVisible = true
        this.isSkuBlockOutlined = false
        const setCustomSku = async () => {
          this.activeSku = this.baseSku
          this.skuProducts = [this.baseSku]
          await this.getProduct(this.baseSku.code, true)
        }
        if (this.isForceCustomActive && this.hasBaseSku) {
          await setCustomSku()
        } else {
          if (!code) {
            this.activeSku = {}
          } else {
            const isCustomProduct = code === this.baseSku.code
            if (isCustomProduct) this.activeSku = this.baseSku
            await this.getProduct(code, isCustomProduct)
            if (!isCustomProduct && !this.tableLabelUnit) {
              const gender = this.productData.customizations.find(
                (el) => el.serverField === 'gender',
              )
              if (gender?.selected) this.setTableLabelUnit(gender.selected)
            }
          }
          await this.updateProductSkus({ isLoadAllSkus })
        }
        if (this.isForceCustomActive && this.hasBaseSku) await setCustomSku()

        this.$router.replace(this.activeSku.url || this.baseSku.url || '/' + this.slug)

        this.validateConfigurationsChange(
          this.productData,
          this.productData.customizations,
          this.configurationsRelationshipEntries,
          this.resetTableValue,
        )
      } finally {
        this.activeSetSkuOperations = this.activeSetSkuOperations.filter((el) => el !== uid)
        if (!this.activeSetSkuOperations.length) this.isLoaderVisible = false
      }
    },
    async getProduct(code, custom = false) {
      const productData = {
        code,
        ...this.getPriceParams({
          salesUnit: this.activeSku.salesUnit,
          qtyTable: this.qtyTable,
          customizations: this.productData.customizations,
          isCellProduct: this.productData.isCellProduct,
          isVialsPerDonorProduct: this.productData.isVialsPerDonorProduct,
        }),
      }
      let product
      try {
        const productDataRaw = await this.productStore.getProductData(productData)
        product = this.transformSku(productDataRaw)
      } catch (err) {
        this.isGetProductErrorOccurred = true
        const skuList = await this.getSkuList()
        this.skuProducts = skuList
        const requestedProduct = this.skuProducts.find((skuProduct) => skuProduct.code === code)
        if (requestedProduct) product = requestedProduct
        throw err
      } finally {
        if (custom) {
          this.activeSku.salesUnit = product.salesUnit
        } else {
          this.activeSku = { ...product }
          const serverFieldCustomizations = this.getAllCustomizations.filter((el) => el.serverField)
          serverFieldCustomizations.forEach((customization) => {
            const productFieldEntries = Object.entries(this.activeSku)
            const productServerFieldEntry = productFieldEntries.find(
              ([key]) => key === customization.serverField,
            )
            if (!productServerFieldEntry) return
            const productServerFieldValue = productServerFieldEntry[1]
            const isArrayValue = Array.isArray(productServerFieldValue)
            const valueToSet = isArrayValue
              ? productServerFieldValue.find((superCategoryItem) => {
                  return customization.options.find(
                    (customizationsOption) =>
                      customizationsOption?.toString()?.toLowerCase() ===
                      superCategoryItem?.toString()?.toLowerCase(),
                  )
                })
              : customization.options?.find(
                  (option) =>
                    option?.toString()?.toLowerCase() ===
                    productServerFieldValue?.toString()?.trim()?.toLowerCase(),
                )
            if (valueToSet) {
              customization.selected = valueToSet
              customization.isHidden = false
              this.checkParentSelection(
                customization,
                this.getCustomizations,
                this.configurationsRelationshipEntries,
              )
            }
          })
          this.updateQtyTableBySelectedCustomizations(
            this.getCustomizations,
            this.productData,
            this.qtyTable,
            this.resetTableValue,
          )
        }
      }
    },
    async getSkuList({ isLoadAllSkus = false } = {}) {
      const { hits } = await this.$search.service.getProductIndex().search('', {
        filters: this.getSkuFiltersString(
          this.slug,
          !isLoadAllSkus,
          this.getActiveServerCustomizations(this.getCustomizations),
        ),
        hitsPerPage: 999,
        clickAnalytics: true,
      })
      const sortedSkus = hits.sort((a, b) => a.name?.localeCompare(b?.name)).map(this.transformSku)
      return this.hasBaseSku ? [...sortedSkus, this.baseSku] : sortedSkus
    },
    async updateProductSkus({ isLoadAllSkus = false } = {}) {
      const skuList = await this.getSkuList({ isLoadAllSkus })
      this.skuProducts = skuList
      if (this.skuProducts.length === 2 && !this.productData.noCustomSku) {
        if (this.isGetProductErrorOccurred) return
        this.activeSku = this.skuProducts[0]
        await this.getProduct(this.activeSku.code)
      } else if (this.skuProducts.length === 1) {
        this.activeSku = this.skuProducts[0]
        await this.getProduct(this.activeSku.code, true)
      }
    },
    debouncePriceUpdate: debounce(async function () {
      if (this.sessionStore.isUserLoggedIn) await this.setSku(this.activeSku.code)
    }, 500),
    isValueSelected(item) {
      return typeof item.selected === 'number' || item.selected
    },
    getFieldValue(item) {
      return this.isValueSelected(item) ? item.selected : 'Not Selected'
    },
    async onCustomizationValueChange({ customization, value }) {
      const widgetType = customization.widgetType

      if (customization.isNumericFreeText && typeof value === 'string') value = value.trim()

      customization.selected =
        widgetType === 'chips' || widgetType === 'cards'
          ? customization.selected === value
            ? null
            : value
          : value

      if (widgetType === 'chips' || widgetType === 'dropList') {
        if (customization.title)
          this.displayHiddenFreeText(customization.title, this.productData?.customizations)
        if (customization.serverField === 'gender' && customization.selected) {
          this.setTableLabelUnit(customization.selected)
        }
      }
      this.validateConfigurationsChange(
        this.productData,
        this.productData.customizations,
        this.configurationsRelationshipEntries,
        this.resetTableValue,
      )

      this.updateQtyTableBySelectedCustomizations(
        this.getCustomizations,
        this.productData,
        this.qtyTable,
        this.resetTableValue,
      )

      this.validateAllCustomizationValues()

      await this.debounceUpdateSkuBox(customization)
      await this.debouncePriceUpdate()
    },
    validateAllCustomizationValues() {
      const allFailedComparisonTest = []
      const allFailedNumericTest = []
      this.getCustomizations.forEach((customization) => {
        const { failedComparisonTest, failedNumericTest } = this.validateCustomizationValue(
          customization,
          this.productData.isCellProduct,
          this.getCustomizations,
          this.qtyTable,
        )
        allFailedComparisonTest.push(...failedComparisonTest)
        allFailedNumericTest.push(...failedNumericTest)
      })
      this.failedComparisonTest = allFailedComparisonTest
      this.failedNumericTest = allFailedNumericTest
    },
    debounceUpdateSkuBox: debounce(async function (item) {
      await this.updateSkuBox(item)
    }, 500),
    async updateSkuBox(customization) {
      try {
        this.isLoaderVisible = true
        const isServerCustomization = this.activeSkuFields.includes(customization.serverField)
        if (isServerCustomization || this.isForceCustomActive) {
          await this.unsetSku()
        } else {
          await this.updateProductSkus()
        }
        const isActiveSkuNotListed = !this.skuProducts.some(
          ({ code }) => code === this.activeSku.code,
        )
        const isBaseSkuSelectedWhenOtherAvailable =
          this.skuProducts.length > 1 && this.isBaseSkuSelected
        if (isActiveSkuNotListed || isBaseSkuSelectedWhenOtherAvailable) this.activeSku = {}
      } finally {
        this.isLoaderVisible = false
      }
    },
    async withPriceUpdate(cb) {
      const oldPriceParams = this.getPriceParams({
        salesUnit: this.activeSku.salesUnit,
        qtyTable: this.qtyTable,
        customizations: this.productData.customizations,
        isCellProduct: this.productData.isCellProduct,
        isVialsPerDonorProduct: this.productData.isVialsPerDonorProduct,
      })

      await cb()

      const newPriceParams = this.getPriceParams({
        salesUnit: this.activeSku.salesUnit,
        qtyTable: this.qtyTable,
        customizations: this.productData.customizations,
        isCellProduct: this.productData.isCellProduct,
        isVialsPerDonorProduct: this.productData.isVialsPerDonorProduct,
      })
      const isPriceParamsChanged = Object.entries(newPriceParams).some(
        ([key, value]) => oldPriceParams[key] !== value,
      )
      if (isPriceParamsChanged) await this.debouncePriceUpdate()
    },
    async initData(entry) {
      const skuFromUrl = this.getProductSkuFromSlug
      try {
        this.productData = this.transformProduct(entry)
        if (!this.productData) throw new Error('Product data is not found')
        this.baseSku = this.productData.noCustomSku
          ? {}
          : {
              code: this.productData.sageCode,
              name: this.productData.title,
              url: `/${this.slug}`,
            }

        const snapshot = this.productStore.getCustomizationSnapshot(window.location.pathname)
        const customizationsFromSnapshot = snapshot?.customizations

        if (this.cartStore.cartEditableEntry?.code) {
          this.productEntry = JSON.parse(JSON.stringify(this.cartStore.cartEditableEntry))
          this.cartStore.clearUpdateProduct()
          this.isEditableProduct = true
          this.productQty = this.productEntry.qty
        }

        if (customizationsFromSnapshot) {
          this.productData.customizations.forEach((el) => {
            const attr = customizationsFromSnapshot.find((item) => item.title === el.title)
            if (attr) {
              el.selected = el.cardsOptions
                ? el.cardsOptions.find((card) => card.title === attr.selected)
                : attr.selected
              if (el.widgetType === 'qty') el.selected = +el.selected
              if (el.textarea && el.selected) el.textarea.uiDisplay = true
              if (el.serverField === 'gender' && el.selected) this.setTableLabelUnit(el.selected)
            }
          })
        }

        this.validateConfigurationsChange(
          this.productData,
          this.productData.customizations,
          this.configurationsRelationshipEntries,
          this.resetTableValue,
        )

        this.updateQtyTableBySelectedCustomizations(
          this.getCustomizations,
          this.productData,
          this.qtyTable,
          this.resetTableValue,
        )

        try {
          const skuToSet = skuFromUrl || snapshot?.selectedSku || this.baseSku?.code || null
          if (skuFromUrl) {
            await this.setSku(skuToSet, { isLoadAllSkus: true })
          }
          await this.setSku(skuToSet)
        } catch {
          await this.setSku(this.baseSku?.code || null)
        }

        if (this.sessionStore.isUserLoggedIn) {
          this.sessionStore.addRecentlyViewedProduct(this.productData.slug)
        }
      } finally {
        const isAppliedSkuNotFromUrl =
          skuFromUrl && this.activeSku?.code && this.activeSku.code !== skuFromUrl
        if (isAppliedSkuNotFromUrl) {
          this.resetAllCustomizations()
        }
        this.isPageLoading = false
        this.isGlobalLoaderVisible = false
      }
    },
    toggleGroup(groupIdx) {
      const groupToToggle = this.productData.groupList[groupIdx]
      if (groupToToggle) groupToToggle.collapsed = !groupToToggle.collapsed
    },
  },
  validations() {
    return {
      getCustomizations: {
        $each: helpers.forEach({
          selected: {
            required: requiredIf(function (value, model) {
              return model.required
            }),
            isPositiveInteger(value, model) {
              return model.widgetType === 'qty'
                ? typeof value === 'number' && value >= model.minValue
                : true
            },
            minValue(value, model) {
              return model.widgetType === 'qty'
                ? typeof value === 'number' && typeof model.maxValue === 'number'
                  ? value >= model.minValue
                  : true
                : true
            },
            maxValue(value, model) {
              return model.widgetType === 'qty'
                ? typeof value === 'number' && typeof model.maxValue === 'number'
                  ? value <= model.maxValue
                  : true
                : true
            },
            isIncrement(value, model) {
              if (model.widgetType !== 'qty') return true
              if (typeof value !== 'number') return false
              if (!model.increment || model.increment === 0) return true

              const epsilon = 0.0001
              return (
                Math.abs((value / model.increment) % 1) < epsilon ||
                Math.abs(((value / model.increment) % 1) - 1) < epsilon
              )
            },
          },
        }),
      },
      failedNumericTest: {
        maxLength: maxLength(0),
      },
      failedComparisonTest: {
        maxLength: maxLength(0),
      },
      productQty: {
        integer,
        minValue: minValue(1),
        maxValue: () => this.isMaxProductQuantityValid,
      },
    }
  },
}
</script>
